import React from 'react';
import { DocumentOutlineIcon, DocumentCheckOutlineIcon } from '@applyboard/ui-icons';
import { Flex, Text, Card as FrameCard, Tag } from '@applyboard/crystal-ui';
import { TruncatedButtonLink } from '~/components/uie-fix/TruncatedButtonLink';
import { Attachment } from '~/data/useApplication';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import moment from 'moment';
import { Card } from './Card';
import { Decision } from '@ab/ams-school-api/src/resources/applications/studentDecisions';
import { getReplacedOnDate } from './DecisionLetters.utils';

type DecisionLetterCardProps = {
  attachmentDocument: Attachment;
  decision?: Decision;
  rejectionReason?: string;
  replacedOnDate?: Date;
};

function DecisionLetterCard({
  attachmentDocument,
  decision,
  rejectionReason,
  replacedOnDate,
}: DecisionLetterCardProps) {
  const isOfferLetter =
    attachmentDocument.type.code === 'PRE_OFFER_LETTER' ||
    attachmentDocument.type.code === 'FINAL_OFFER_LETTER' ||
    attachmentDocument.type.code === 'WAITLIST_LETTER';
  const letterConditions = attachmentDocument.letterInfo?.conditions ?? [];
  const hasLetterInfo = !!attachmentDocument.letterInfo;
  const offerTypeLabel = letterConditions.length > 0 ? 'Conditional' : 'Unconditional';
  const showOfferTypeAndConditions = isOfferLetter && hasLetterInfo;

  function getDocumentTile(attachmentDocument: Attachment) {
    if (attachmentDocument.type.code === 'REJECTION_LETTER') {
      return 'Decline Letter';
    } else if (attachmentDocument.type.code === 'WAITLIST_LETTER') {
      return 'Waitlist Letter';
    } else if (attachmentDocument.type.code === 'FINAL_OFFER_LETTER') {
      return 'Final Offer Letter';
    } else {
      return 'Initial Offer Letter';
    }
  }

  function offerStatusTag(decision: Decision | undefined, attachmentDocument: Attachment) {
    if (attachmentDocument.revokedAt) {
      return (
        <Flex.Item>
          <Tag intent="negative" size="sm">
            Revoked
          </Tag>
        </Flex.Item>
      );
    } else if (
      attachmentDocument.type.code === 'PRE_OFFER_LETTER' ||
      attachmentDocument.type.code === 'FINAL_OFFER_LETTER'
    ) {
      return (
        <Flex.Item>
          {attachmentDocument.isActive ? (
            attachmentDocument.type.code === 'PRE_OFFER_LETTER' &&
            getDecisionStatusIcon(decision?.studentResponse || 'PENDING')
          ) : (
            <Tag intent="magic" size="sm">
              Replaced
            </Tag>
          )}
        </Flex.Item>
      );
    }
    return null;
  }
  return (
    <FrameCard>
      <Flex direction="column" gap={4}>
        <Flex direction="row" justify="between" gap={8}>
          <Flex.Item>
            <Flex direction="row" align="center" justify="center" gap={6}>
              <Flex.Item>
                <Text variant="titleS" contrast="high" intent="secondary">
                  {getDocumentTile(attachmentDocument)}
                </Text>
              </Flex.Item>
              <Flex.Item>{offerStatusTag(decision, attachmentDocument)}</Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
        <Flex direction="row" gap={36} justify="start" wrap>
          {showOfferTypeAndConditions && (
            <Flex.Item>
              <Text variant="labelS" intent="secondary">
                Type
              </Text>
              <Text variant="labelM" intent="secondary" contrast="low">
                {offerTypeLabel}
              </Text>
            </Flex.Item>
          )}
          <Flex.Item>
            <Text variant="labelS" intent="secondary">
              Issue date
            </Text>
            <Text variant="labelM" intent="secondary" contrast="low">
              {moment(attachmentDocument.updatedAt).format('MMM DD, YYYY')}
            </Text>
          </Flex.Item>
          {(decision || attachmentDocument.revokedAt) && (
            <>
              {attachmentDocument.revokedAt && (
                <Flex.Item>
                  <Text variant="labelS" intent="secondary">
                    Revoked on
                  </Text>
                  <Text variant="labelM" intent="secondary" contrast="low">
                    {moment(attachmentDocument.revokedAt).format('MMM DD, YYYY')}
                  </Text>
                </Flex.Item>
              )}
              {decision?.decisionDate &&
                decision.studentResponse === 'ACCEPTED' &&
                attachmentDocument.isActive !== false && (
                  <Flex.Item>
                    <Text variant="labelS" intent="secondary">
                      Accepted on
                    </Text>
                    <Text variant="labelM" intent="secondary" contrast="low">
                      {moment(decision.decisionDate).format('MMM DD, YYYY')}
                    </Text>
                  </Flex.Item>
                )}
              {decision?.decisionDate &&
                decision.studentResponse === 'REJECTED' &&
                attachmentDocument.isActive !== false && (
                  <Flex.Item>
                    <Text variant="labelS" intent="secondary">
                      Rejected on
                    </Text>
                    <Tag intent="secondary" size="sm">
                      {moment(decision.decisionDate).format('MMM DD, YYYY')}
                    </Tag>
                  </Flex.Item>
                )}
              {decision?.reason && (
                <Flex.Item>
                  <Text variant="labelS" intent="secondary">
                    Reason of rejection
                  </Text>
                  <Text variant="labelM" intent="secondary" contrast="low">
                    {decision?.reason}
                  </Text>
                </Flex.Item>
              )}
            </>
          )}
          {!attachmentDocument.revokedAt && !attachmentDocument.isActive && replacedOnDate && (
            <Flex.Item>
              <Text variant="labelS" intent="secondary">
                Replaced on
              </Text>
              <Text variant="labelM" intent="secondary" contrast="low">
                {moment(replacedOnDate).format('MMM DD, YYYY')}
              </Text>
            </Flex.Item>
          )}
          {attachmentDocument.type.code === 'REJECTION_LETTER' && (
            <Flex.Item>
              <Text variant="labelS" intent="secondary">
                Reason
              </Text>
              <Text variant="labelM" intent="secondary" contrast="low">
                {rejectionReason}
              </Text>
            </Flex.Item>
          )}
        </Flex>
        <Flex direction="row" justify="start">
          <Flex.Item>
            <TruncatedButtonLink
              aria-label={`Download ${attachmentDocument.fileName} document`}
              emphasis="highlighted"
              href={attachmentDocument.downloadUrl!}
              intent={attachmentDocument.validationStatus === 'VALID' ? 'positive' : 'primary'}
              size="lg"
              leadIcon={DocumentCheckOutlineIcon}
              width="hug"
              mode="override_user_choice_and_open_in_new_tab"
            >
              {attachmentDocument.fileName}
            </TruncatedButtonLink>
          </Flex.Item>
        </Flex>
      </Flex>
    </FrameCard>
  );
}
export function DecisionLetters() {
  const {
    application: {
      preOfferLetters,
      rejectionLetters,
      studentDecisions,
      waitlistLetters,
      finalOfferLetters,
      rejectionReason,
    },
  } = useAppProcessing();

  const preOfferLettersArray = Array.isArray(preOfferLetters) ? preOfferLetters : [];
  const finalOfferLettersArray = Array.isArray(finalOfferLetters) ? finalOfferLetters : [];
  const waitlistLetterArray = Array.isArray(waitlistLetters) ? waitlistLetters : [];
  const rejectionLetterArray = Array.isArray(rejectionLetters) ? rejectionLetters : [];
  const combinedLetters = [
    ...preOfferLettersArray,
    ...finalOfferLettersArray,
    ...waitlistLetterArray,
    ...rejectionLetterArray,
  ];
  const activeLetters = combinedLetters
    .filter((l) => l.isActive !== false)
    .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime());
  const inactiveLetters = combinedLetters
    .filter((l) => l.isActive === false)
    .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime());

  function getDecisionForLetter(letter: Attachment): Decision | undefined {
    let decision;
    if (letter.type.code === 'PRE_OFFER_LETTER') {
      decision = studentDecisions?.preOfferDecision;
    } else if (letter.type.code === 'FINAL_OFFER_LETTER') {
      decision = studentDecisions?.finalOfferDecision;
    }
    if (decision) {
      return {
        ...decision,
        decisionDate: decision.decisionDate ? decision.decisionDate : undefined,
      };
    }
    return undefined;
  }

  return (
    <Card hideChevron id="decisionLetters" icon={DocumentOutlineIcon} title="Decision Letters">
      <Flex gap={8} direction="column">
        {[...activeLetters, ...inactiveLetters].map((offerLetter, idx) => (
          <Flex.Item key={`${offerLetter.type.code}-${offerLetter.updatedAt}-${idx}`}>
            <DecisionLetterCard
              attachmentDocument={offerLetter}
              decision={getDecisionForLetter(offerLetter)}
              rejectionReason={rejectionReason}
              replacedOnDate={getReplacedOnDate(offerLetter, combinedLetters)}
            />
          </Flex.Item>
        ))}
      </Flex>
    </Card>
  );
}

const getDecisionStatusIcon = (decisionStatus: string) => {
  switch (decisionStatus) {
    case 'ACCEPTED':
      return (
        <Tag intent="positive" size="sm">
          Accepted
        </Tag>
      );
    case 'REJECTED':
      return (
        <Tag intent="negative" size="sm">
          Student Rejected Offer
        </Tag>
      );
    case 'PENDING':
      return (
        <Tag intent="warning" size="sm">
          Pending review
        </Tag>
      );
    default:
      return;
  }
};
